// @ts-ignore
import shakaModule from "shaka-player"

interface ITrack {
  height: number
}

export interface IShakaPlayer {
  currentTime: number
  addEventListener(event: "error", cb: (error: any) => void): void
  addEventListener(event: "buffering", cb: (isBuffering: boolean) => void): void
  load(manifestUri: string): Promise<void>
  getMediaElement(): HTMLVideoElement
  setAutoSwitchQuality(autoSwitch: boolean): void
  selectVideoTrack(trackId: string): void
  getBufferedInfo(): {
    total: { start: number; end: number }[]
    audio: { start: number; end: number }[]
    video: { start: number; end: number }[]
    text: { start: number; end: number }[]
  }
  isBuffering(): void
  configure(config: Partial<{ abr: { enabled: boolean } }>): void
  getVariantTracks(): ITrack[]
  /**
   *
   * @param clearBuffer bool - whether or not to retain currently buffered video
   * @param safeMargin number - if clear buffer is true, how much of current buffer to play prior to track switch
   */
  selectVariantTrack(trackId: number | ITrack, clearBuffer?: boolean, safeMargin?: number): void
}
interface IShaka {
  Player: {
    new (element: HTMLVideoElement | null): IShakaPlayer
  }
}

const shaka = shakaModule as any as IShaka

export type IPlayer = IShakaPlayer

interface ICreatePlayerOptions {
  element: HTMLVideoElement | null
  manifestUri: string
  forceUpdate(): void
  handleBuffering?(status: boolean): void
  playbackTimeUpdated?(info: { currentTime: number; duration: number }): void
  videoPlayed?(info: { currentTime: number; duration: number }): void
  videoPaused?(info: { currentTime: number; duration: number }): void
}
export async function createPlayer({
  element,
  manifestUri,
  forceUpdate,
  playbackTimeUpdated,
  videoPaused,
  videoPlayed,
}: ICreatePlayerOptions): Promise<IShakaPlayer> {
  const player = new shaka.Player(element)
  ;(window as any).player = player
  // Listen for error events.
  player.addEventListener("error", error => {
    console.error("THERE WAS AN ERROR FROM THE SHAKA PLAYER")
    console.error(error)
  })

  player.addEventListener("buffering", () => {
    forceUpdate()
  })

  try {
    await player.load(manifestUri)
    // This runs if the asynchronous load is successful.
  } catch (error) {
    // onError is executed if the asynchronous load fails.
    console.error("THERE WAS AN ERROR LOADING THE MANIFEST")
    console.error(error)
  }

  element?.addEventListener("timeupdate", () => {
    playbackTimeUpdated?.({ currentTime: element?.currentTime, duration: element?.duration })
    forceUpdate()
  })
  element?.addEventListener("play", () => {
    videoPlayed?.({ currentTime: element?.currentTime, duration: element?.duration })
    forceUpdate()
  })
  element?.addEventListener("pause", () => {
    videoPaused?.({ currentTime: element?.currentTime, duration: element?.duration })
    forceUpdate()
  })
  return player
}

export function onPlayerTimeUpdate(
  player: IPlayer | null,
  cb: (event: { target: HTMLVideoElement }) => void,
): () => void {
  if (isPlayerReady(player)) {
    const element = player.getMediaElement()
    element.addEventListener("timeupdate", cb as any)

    return () => element.removeEventListener("timeupdate", cb as any)
  } else {
    return () => void 0
  }
}

export function togglePlay(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    const el = player.getMediaElement()
    return el.paused ? el.play() : el.pause()
  }
}

export function isPaused(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    return player.getMediaElement().paused
  } else {
    return true
  }
}

export function isPlayerReady(player: IPlayer | null): player is IPlayer {
  return !!player
}

export function getCurrentTime(player: null | IPlayer) {
  if (isPlayerReady(player)) {
    return player.getMediaElement().currentTime
  }
  return 0
}

export function getDuration(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    return player.getMediaElement().duration
  }
  return -1
}

export function getVolume(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    return player.getMediaElement().volume
  } else {
    return 0
  }
}

export function setVolume(player: IPlayer | null, volume: number) {
  if (isPlayerReady(player)) {
    player.getMediaElement().volume = volume
  }
}

export function setQualityToAuto(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    player.configure({ abr: { enabled: true } })
  }
}
export function setQualityByVideoHeight(player: IPlayer | null, quality: number) {
  if (isPlayerReady(player)) {
    player.configure({ abr: { enabled: false } })
    const trackId = player.getVariantTracks().find(variant => variant.height === quality)
    if (!trackId) {
      return
    }
    player.selectVariantTrack(trackId, true, 2)
  }
}

// export function getVideoBitrateInfoList(player: IPlayer): dashjs.BitrateInfo[] {
//   if (isPlayerReady(player)) {
//     return player.getBitrateInfoListFor("video")
//   } else {
//     return []
//   }
// }

export function getBufferedPercentage(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    return player.getBufferedInfo().total
  }
  return []
}

export function setSeekPosition(player: IPlayer | null, time: number) {
  if (isPlayerReady(player)) {
    player.getMediaElement().currentTime = time
  }
}

export function timeAsPercentage(player: IPlayer | null, time: number) {
  return (time / getDuration(player)) * 100
}

export function isBuffering(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    return player.isBuffering()
  }
  return false
}

export function play(player: IPlayer | null) {
  if (isPlayerReady(player)) {
    const el = player.getMediaElement()
    return el.play()
  }
}
